import styles from './index.module.scss';
import {
  Button,
  Dropdown,
  EllipsisSpan,
  Form,
  Modal,
  Paging,
  Select,
  Table,
  Tag,
  useSearchParams,
  useUpdate,
} from '@maxtropy/components';
import {
  apiV2GgdBatchOperateStatusPost,
  apiV2GgdDistributeTimeTaskPost,
  apiV2LightmapAreaListPost,
  apiV2LightmapCountPost,
  apiV2LightmapDeviceList3Post,
  apiV2LightmapDevicePage3Post,
  V2LightmapDevicePage3PostResponse,
} from '@maxtropy/device-customer-apis-v2';
import gdd from './image/1.png';
import circuit from './image/2.png';
import light from './image/3.png';
import { useRequest } from 'ahooks';
import { isNil } from 'lodash-es';
import { ProgressBar } from './components/ProgressBar';
import { Space } from 'antd';
import { DistributeStatus, StatusType } from '@/pages/PowerLightingControl/utils';
import { Modal as AntModal } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { Key, useMemo, useState } from 'react';
import ScheduledTaskModal from '@/pages/PowerLightingControl/components/ScheduledTaskModal';
import CardBox from '../../../CardBox';
export type DeviceItem = Exclude<V2LightmapDevicePage3PostResponse['list'], undefined>[number];

const getFullScreenPopupContainer = () => document.getElementById('full_screen_box') ?? document.body;

const LeftBox = () => {
  const [modalApi, modalContextHolder] = AntModal.useModal();
  const [selectedRows, setSelectedRows] = useState<DeviceItem[]>([]);
  const [update, updateFn] = useUpdate();
  const pagingInfo = useSearchParams(50);

  const { pageOffset, pageSize, setTotalCount } = pagingInfo;

  const [deviceId, setDeviceId] = useState<number>();
  const [currentId, setCurrentId] = useState<number>();
  const [currentDeviceId, setCurrentDeviceId] = useState<number>();
  const [showScheduledTaskModal, setShowScheduledTaskModal] = useState<boolean>(false);
  const [areaId, setAreaId] = useState<number>();

  const { data: countData } = useRequest(() =>
    apiV2LightmapCountPost({}).then(res => {
      return res;
    })
  );

  const { data, loading } = useRequest(
    () =>
      apiV2LightmapDevicePage3Post({
        deviceId,
        areaIds: areaId ? [areaId] : undefined,
        page: pageOffset,
        size: pageSize,
      }).then(res => {
        setTotalCount(res.total ?? 0);
        return res.list;
      }),
    {
      refreshDeps: [deviceId, areaId, pageOffset, pageSize, update],
    }
  );

  // 车间选择
  const { data: areaList } = useRequest(async () => {
    const res = await apiV2LightmapAreaListPost({});
    return res.list;
  });

  const areaOptions = useMemo(() => {
    return (areaList ?? []).map(item => ({
      label: item.zoneName,
      value: item.id,
    }));
  }, [areaList]);

  // 设备选择
  const { data: deviceList } = useRequest(async () => {
    const res = await apiV2LightmapDeviceList3Post({});
    return res.list;
  });

  const deviceOptions = useMemo(() => {
    return (deviceList ?? []).map(item => ({
      label: item.deviceName,
      value: item.deviceId,
    }));
  }, [deviceList]);

  const makeLoadingModel = () =>
    modalApi.info({
      className: 'loading-modal',
      title: '正在执行',
      content: (
        <div className="loading-modal-content">
          <div className="loading-modal-icon">
            <LoadingOutlined />
          </div>
          <div className="loading-modal-txt">正在执行中</div>
        </div>
      ),
    });

  const remoteControl = (deviceIds: number[], deviceNames: string[], status: StatusType) => {
    if (!deviceIds.length) return;
    Modal.confirm({
      title: (
        <span style={{ color: 'var(--mx-text-base-color)' }}>
          确定要下发<span style={{ color: '#E64242' }}>{status === StatusType.CLOSED ? '远程开启' : '远程关闭'}</span>
          指令到{deviceNames.join('、')}设备吗？
        </span>
      ),
      okText: '确定',

      onOk() {
        const modal = makeLoadingModel();
        setSelectedRows([]);
        apiV2GgdBatchOperateStatusPost({ deviceIds, status })
          .then(_ => {
            updateFn();
          })
          .finally(() => {
            modal.destroy();
          });
      },
    });
  };

  const distributeTask = (id: number) => {
    if (isNil(id)) return;
    apiV2GgdDistributeTimeTaskPost({
      id: id!,
    }).then(res => {
      updateFn();
    });
  };

  const rowSelection = {
    preserveSelectedRowKeys: true,
    onChange: (_: Key[], selectedRows: DeviceItem[]) => {
      setSelectedRows(selectedRows);
    },
    selectedRowKeys: selectedRows?.map(item => item.deviceId as Key) ?? [],
  };

  const buildColumns = [
    {
      title: '车间',
      dataIndex: 'zoneName',
      ellipsis: { showTitle: true },
      render: (v: string) => <EllipsisSpan value={v} />,
    },
    {
      title: '控制设备名称',
      dataIndex: 'deviceName',
      ellipsis: { showTitle: true },
      render: (v: string) => <EllipsisSpan value={v} />,
    },
    {
      title: '闸位状态',
      dataIndex: 'intStatus',
      ellipsis: { showTitle: true },
      render: (v: number) =>
        isNil(v) ? (
          '--'
        ) : v === 0 ? (
          <Tag border="solid" color="default">
            关闭
          </Tag>
        ) : v === 1 ? (
          <Tag border="solid" color="success">
            开启
          </Tag>
        ) : (
          <Tag border="solid" color="warning">
            离线
          </Tag>
        ),
    },
    {
      title: '操作',
      dataIndex: 'operation',
      width: 88,
      render: (value: any, record: any) => {
        return (
          <Space size={16}>
            <Dropdown
              type="link"
              getPopupContainer={getFullScreenPopupContainer}
              menu={{
                items: [
                  {
                    key: '1',
                    label: '远程关闭',
                    disabled:
                      isNil(record.status) || record.status === StatusType.OPEN || record.status === StatusType.PENDING,
                    onClick: () => {
                      remoteControl([record.deviceId], [record.deviceName], StatusType.OPEN);
                    },
                  },
                  {
                    key: '2',
                    label: '远程开启',
                    disabled:
                      isNil(record.status) ||
                      record.status === StatusType.CLOSED ||
                      record.status === StatusType.PENDING,
                    onClick: () => {
                      remoteControl([record.deviceId], [record.deviceName], StatusType.CLOSED);
                    },
                  },
                  {
                    key: '3',
                    label: '定时任务',
                    disabled:
                      isNil(record.status) ||
                      !record.canSetTimeTask ||
                      record.taskDistributeStatus === DistributeStatus.PENDING ||
                      record.taskDistributeStatus === DistributeStatus.FINISH,
                    onClick: () => {
                      setCurrentId(record.id);
                      setCurrentDeviceId(record.deviceId);
                      setShowScheduledTaskModal(true);
                    },
                  },
                  {
                    key: '4',
                    label: '下发定时任务',
                    disabled:
                      isNil(record.status) ||
                      !record.canSetTimeTask ||
                      record.taskDistributeStatus === DistributeStatus.PENDING ||
                      record.taskDistributeStatus === DistributeStatus.FINISH,
                    onClick: () => {
                      distributeTask(record.id);
                    },
                  },
                ],
              }}
            >
              控制
            </Dropdown>
          </Space>
        );
      },
    },
  ];

  return (
    <div className={styles.left_wrapper} style={{ gap: 0 }}>
      <CardBox title="照明设备统计" style={{ height: 'fit-content', flex: 'unset' }}>
        <div style={{ display: 'flex', flexDirection: 'column', marginTop: '10px' }}>
          {/* 车间照明 - 第一个 */}
          <div className={styles.ggdBox}>
            <img src={circuit} className={styles.Img} />
            <div className={styles.Info}>
              <div style={{ fontSize: '14px' }}>车间照明</div>
              <div className={styles.countText}>
                {!isNil(countData?.circuitData?.count) ? countData?.circuitData?.count : '-'}
              </div>
            </div>
            {/* 分隔线 */}
            <div
              style={{
                width: 1,
                height: 55,
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                margin: '0 8px',
                borderRadius: 1,
              }}
            />
            {/* 进度条在右侧 */}
            <div className={styles.ggdProgress} style={{ flex: 1 }}>
              <ProgressBar
                mergeCount={countData?.circuitData?.openCount ?? 0}
                splitCount={countData?.circuitData?.closeCount ?? 0}
                offlineCount={countData?.circuitData?.offlineCount ?? 0}
              />
            </div>
          </div>
          {/* 高杆灯 - 第二个 */}
          <div className={styles.ggdBox}>
            <img src={gdd} className={styles.Img} />
            <div className={styles.Info}>
              <div>高杆灯</div>
              <div className={styles.countText}>
                {!isNil(countData?.ggdData?.count) ? countData?.ggdData?.count : '-'}
              </div>
            </div>
            {/* 分隔线 */}
            <div
              style={{
                width: 1,
                height: 55,
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                margin: '0 8px',
                borderRadius: 1,
              }}
            />
            {/* 进度条在右侧 */}
            <div className={styles.ggdProgress} style={{ flex: 1 }}>
              <ProgressBar
                mergeCount={countData?.ggdData?.openCount ?? 0}
                splitCount={countData?.ggdData?.closeCount ?? 0}
                offlineCount={countData?.ggdData?.offlineCount ?? 0}
              />
            </div>
          </div>
          {/* 太阳能路灯 - 第三个 */}
          <div className={styles.ggdBox}>
            <img src={light} className={styles.Img} />
            <div className={styles.Info}>
              <div style={{ fontSize: '14px' }}>太阳能路灯</div>
              <div className={styles.countText}>
                {!isNil(countData?.lightData?.count) ? countData?.lightData?.count : '-'}
              </div>
            </div>
            {/* 分隔线 */}
            <div
              style={{
                width: 1,
                height: 55,
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                margin: '0 8px',
                borderRadius: 1,
              }}
            />
            {/* 进度条在右侧 */}
            <div className={styles.ggdProgress} style={{ flex: 1 }}>
              <ProgressBar
                mergeCount={countData?.lightData?.openCount ?? 0}
                splitCount={countData?.lightData?.closeCount ?? 0}
                offlineCount={countData?.lightData?.offlineCount ?? 0}
              />
            </div>
          </div>
        </div>
      </CardBox>
      <CardBox
        title="车间照明控制"
        extraContent={
          <Button
            type="link"
            onClick={() => {
              window.open(`/power/lighting/control`, '_blank');
            }}
          >
            详情{'>>'}
          </Button>
        }
      >
        <div className={styles.search_wrapper}>
          <Form.Item label="车间名称" style={{ flex: 1, margin: '16px 0' }}>
            <Select
              placeholder="请选择"
              optionFilterProp="label"
              showSearch
              allowClear
              options={areaOptions}
              onChange={(value: number) => {
                setAreaId(value);
                setSelectedRows([]);
              }}
            />
          </Form.Item>
          <Form.Item label="控制设备名称" style={{ flex: 1, margin: '16px 0' }}>
            <Select
              placeholder="请选择"
              optionFilterProp="label"
              showSearch
              allowClear
              options={deviceOptions}
              onChange={(value: number) => {
                setDeviceId(value);
                setSelectedRows([]);
              }}
            />
          </Form.Item>
        </div>
        <Space size={8} style={{ marginBottom: 12 }}>
          <div
            className={styles.batch_btn}
            onClick={() =>
              remoteControl(
                (selectedRows ?? []).map(i => i.deviceId!),
                (selectedRows ?? []).map(i => i.deviceName!),
                StatusType.CLOSED
              )
            }
          >
            批量开启
          </div>
          <div
            className={styles.batch_btn}
            onClick={() =>
              remoteControl(
                (selectedRows ?? []).map(i => i.deviceId!),
                (selectedRows ?? []).map(i => i.deviceName!),
                StatusType.OPEN
              )
            }
          >
            批量关闭
          </div>
        </Space>
        <Table
          loading={loading}
          rowKey="deviceId"
          columns={buildColumns}
          scroll={{ y: `20vh` }}
          dataSource={data}
          rowSelection={{
            type: 'checkbox',
            ...rowSelection,
          }}
        ></Table>
        <Paging pagingInfo={pagingInfo} showSizeChanger={false} />
        {showScheduledTaskModal && (
          <ScheduledTaskModal
            id={currentId}
            deviceId={currentDeviceId}
            open={showScheduledTaskModal}
            setOpen={setShowScheduledTaskModal}
            updateFn={updateFn}
          />
        )}
        {modalContextHolder}
      </CardBox>
    </div>
  );
};

export default LeftBox;
